import Cookies from 'universal-cookie'
import Axios from 'axios'
import { store } from '@redux/store'
import { gql } from '@apollo/client'
import { announce } from '@helpers/aria-announce'

const GET_FORWARD_GEOCODE = gql`
  query GetForwardGeocode($input: String!, $layers: String!) {
    getForwardGeocode(input: $input, layers: $layers) {
      latitude
      longitude
      city
      state
      postalCode
    }
  }
`

const GET_CITY_STATE_ZIP = gql`
  query GetCityStateZip($input: String!) {
    getCityStateZip(input: $input) {
      postalCode
    }
  }
`

const GET_CITY_STATE_ZIP_FULL = gql`
  query GetCityStateZip($input: String!) {
    getCityStateZip(input: $input) {
      latitude
      longitude
      country
      county
      city
      postalCode
      stateCode
      state
      street
      layer
      formattedAddress
      addressLabel
    }
  }
`

const cookies = new Cookies()

export const isValidUSStateCode = code => states.some(([full, short]) => short === code) || ['OOM', 'SE'].includes(code)

export const replaceStateShortName = (url, newCode) => {
  if (url.includes('OOM')) {
    return url.replace('OOM', newCode)
  }
  const regex = /[A-Z]{2}-/
  const index = url.search(regex)
  if (index !== -1) {
    const currentCode = url.substring(index, index + 3)

    if (isValidUSStateCode(newCode)) {
      const newURL = url.replace(currentCode, `${newCode}-`)
      return newURL
    }
    return url
  }
  return url
}

export const replaceZone = (url, newZone) => {
  const regex = /zone\d+/
  const index = url.search(regex)
  if (index !== -1) {
    return url.replace(regex, newZone)
  }
  return url
}

export const getForwardGeocode = async (apolloClient, input, layers = 'state,postalCode,locality') => {
  const { data } = await apolloClient.query({
    query: GET_FORWARD_GEOCODE,
    variables: {
      input,
      layers,
    },
  })

  return data?.getForwardGeocode?.[0]
}

export const getCityStateZip = async (apolloClient, input, query = GET_CITY_STATE_ZIP) => {
  const { data } = await apolloClient.query({
    query,
    variables: {
      input,
    },
  })

  return data?.getCityStateZip
}

export const getCurrentLocation = serverCookie =>
  serverCookie?.get('__Host-rtg_location') ||
  cookies.get('__Host-rtg_location') ||
  store.getState().location.rtg_location

export async function geoLocation(apolloClient, address, reload = true) {
  let primaryLocation = null
  let error = false
  try {
    primaryLocation = await getCityStateZip(apolloClient, address, GET_CITY_STATE_ZIP_FULL)
  } catch (err) {
    error = true
    return error
  }

  if (!primaryLocation || (primaryLocation && primaryLocation.country !== 'United States')) {
    error = true
    return error
  }

  const { state, city, postalCode: postal_code, county } = primaryLocation
  cookies.remove('__Host-rtg_region', { path: '/', secure: true, sameSite: 'lax' })
  cookies.remove('rtg_region', { path: '/', secure: true, sameSite: 'lax' })
  cookies.set('__Host-rtg_zip', postal_code, { path: '/', secure: true, sameSite: 'lax' })
  if (process.env.NODE_ENV === 'development') {
    const rtg_location = cookies.get('__Host-rtg_location') ?? {}
    cookies.remove('__Host-rtg_location')
    rtg_location.city = city || ''
    rtg_location.state = state
    rtg_location.county = county
    rtg_location.zip = postal_code
    // Development zip code OOM
    const postal_int = parseInt(postal_code)
    if (postal_int === 80202) {
      rtg_location.region = 'OOM'
    } else if (
      postal_int === 73301 ||
      (postal_int >= 75001 && postal_int <= 79999) ||
      (postal_int >= 88510 && postal_int <= 88589)
    ) {
      rtg_location.region = 'TX'
    } else if (postal_int >= 32004 && postal_int <= 34997) {
      rtg_location.region = 'FL'
    } else {
      rtg_location.region = 'SE'
    }
    cookies.set('__Host-rtg_location', rtg_location, { path: '/', secure: true, sameSite: 'lax' })
  }
  if (reload) {
    const url = new URL(window.location.href)
    // If sortBy we update the URL in the PLP search otherwise reload the page
    if (url.searchParams.get('sortBy')) {
      try {
        const newLocation = await Axios.get(`${process.env.GATSBY_LOCATION_SERVICE_URL}/?zip=${postal_code}`)
        if (newLocation) {
          const { region, price_zone } = newLocation?.data?.response
          let updatedSearchParams = replaceStateShortName(url.searchParams.get('sortBy'), region)
          updatedSearchParams = replaceZone(updatedSearchParams, `zone${price_zone}`)
          url.searchParams.set('sortBy', updatedSearchParams)
          window.location.href = url.href
        }
      } catch {
        error = true
        return error
      }
    } else {
      announce('Location updated, refreshing page')
      window.location.reload()
    }
  }
  return error
}

/**
 * Returns 5 digit area code if zip can be extraced from location
 * @param {string} address
 * @returns {string}
 */

export async function getZipFromSearch(apolloClient, address) {
  const cityStateZip = await getCityStateZip(apolloClient, address)
  return cityStateZip?.postalCode || null
}

export const getLatLng = async (apolloClient, query) => {
  let geocode = null
  let error = false
  try {
    geocode = await getForwardGeocode(apolloClient, query)
    if (!geocode) return null
  } catch (err) {
    error = true
    return error
  }

  return {
    lat: geocode.latitude,
    lng: geocode.longitude,
    state: geocode.state,
    city: geocode.city,
    zip: geocode.postalCode,
  }
}

export const getRegionZone = location => {
  const rtg_location = location || getCurrentLocation()
  return {
    region:
      rtg_location && typeof rtg_location.region !== 'undefined' ? rtg_location.region : process.env.GATSBY_RTG_REGION,
    zone: rtg_location && typeof rtg_location.price_zone !== 'undefined' ? rtg_location.price_zone : 0,
    city: rtg_location && typeof rtg_location.city !== 'undefined' ? rtg_location.city : '',
    state: rtg_location && typeof rtg_location.state !== 'undefined' ? rtg_location.state : '',
    zip: rtg_location && typeof rtg_location.zip !== 'undefined' ? rtg_location.zip : '',
    lat: rtg_location && typeof rtg_location.lat !== 'undefined' ? rtg_location.lat : '',
    long: rtg_location && typeof rtg_location.long !== 'undefined' ? rtg_location.long : '',
    distribution_index:
      rtg_location && typeof rtg_location.distribution_index !== 'undefined' ? rtg_location.distribution_index : '',
  }
}

// returns FL, SE, TX, or OOM
export const getRegion = () => {
  const rtg_location = getCurrentLocation()
  return rtg_location && typeof rtg_location.region !== 'undefined'
    ? rtg_location.region
    : process.env.GATSBY_RTG_REGION
}

export const getZip = () => cookies.get('__Host-rtg_zip') || cookies.get('__Host-rtg_location')?.zip

export const getFullStateName = () => {
  const rtg_location = getCurrentLocation()
  return rtg_location && typeof rtg_location.state !== 'undefined' ? rtg_location.state : 'Florida'
}

export const abbreviateState = stateInput => {
  let state = stateInput
  if (state) {
    state = state.trim()
    if (state.length < 3) {
      state = state.toUpperCase()
      for (let i = 0; i < states.length; i++) {
        if (states[i][1] === state) {
          return state
        }
      }
      return null
    }
    state = state.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
    for (let i = 0; i < states.length; i++) {
      if (states[i][0] === state) {
        return states[i][1]
      }
    }
  }
  return null
}

export const expandState = state => states.find(([full, short]) => short === state)?.[0] || ''
export const getStateAbbr = state => states.find(([full, short]) => full === state)?.[1] || ''

export const states = [
  ['Arizona', 'AZ'],
  ['Alabama', 'AL'],
  ['Alaska', 'AK'],
  ['Arkansas', 'AR'],
  ['California', 'CA'],
  ['Colorado', 'CO'],
  ['Connecticut', 'CT'],
  ['District Of Columbia', 'DC'],
  ['Delaware', 'DE'],
  ['Florida', 'FL'],
  ['Georgia', 'GA'],
  ['Hawaii', 'HI'],
  ['Idaho', 'ID'],
  ['Illinois', 'IL'],
  ['Indiana', 'IN'],
  ['Iowa', 'IA'],
  ['Kansas', 'KS'],
  ['Kentucky', 'KY'],
  ['Louisiana', 'LA'],
  ['Maine', 'ME'],
  ['Maryland', 'MD'],
  ['Massachusetts', 'MA'],
  ['Michigan', 'MI'],
  ['Minnesota', 'MN'],
  ['Mississippi', 'MS'],
  ['Missouri', 'MO'],
  ['Montana', 'MT'],
  ['Nebraska', 'NE'],
  ['Nevada', 'NV'],
  ['New Hampshire', 'NH'],
  ['New Jersey', 'NJ'],
  ['New Mexico', 'NM'],
  ['New York', 'NY'],
  ['North Carolina', 'NC'],
  ['North Dakota', 'ND'],
  ['Ohio', 'OH'],
  ['Oklahoma', 'OK'],
  ['Oregon', 'OR'],
  ['Pennsylvania', 'PA'],
  ['Puerto Rico', 'PR'],
  ['Rhode Island', 'RI'],
  ['South Carolina', 'SC'],
  ['South Dakota', 'SD'],
  ['Tennessee', 'TN'],
  ['Texas', 'TX'],
  ['Utah', 'UT'],
  ['Vermont', 'VT'],
  ['Virginia', 'VA'],
  ['Washington', 'WA'],
  ['West Virginia', 'WV'],
  ['Wisconsin', 'WI'],
  ['Wyoming', 'WY'],
]

export const stateSelectList = states.map(state => state[1]).filter(st => st !== 'PR')
